import { useState, useEffect, useCallback } from 'react';
import {
  PlusIcon,
  MapPinIcon,
  PencilIcon,
  TrashIcon,
  AdjustmentsHorizontalIcon,
} from '@heroicons/react/24/outline';
import { TrophyIcon } from '@heroicons/react/24/solid';
import { supabase } from '../../../lib/supabase';
import { toast } from 'react-hot-toast';
import DeleteConfirmationModal from '../../../components/DeleteConfirmationModal';
import MobileCourseSheet from '../../components/MobileCourseSheet';
import MobileAdminHoleEditor from './MobileAdminHoleEditor';

export interface Course {
  id: string;
  name: string;
  location: string | null;
  city: string | null;
  country: string | null;
  description: string | null;
  par: number;
  holes_count: number;
  website: string | null;
  phone: string | null;
  email: string | null;
  logo_url: string | null;
  image_url: string | null;
  established_year: number | null;
  slope_rating: number | null;
  course_rating: number | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export default function MobileAdminCourses() {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState<Course | null>(null);
  const [deletingCourse, setDeletingCourse] = useState<Course | null>(null);
  const [holeCountsMap, setHoleCountsMap] = useState<Record<string, number>>({});
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);

  const fetchCourses = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.from('courses').select('*').order('name');
      if (error) throw error;
      const list = (data as Course[]) ?? [];
      setCourses(list);

      if (list.length > 0) {
        const ids = list.map((c) => c.id);
        const { data: holeCounts } = await supabase
          .from('holes')
          .select('course_id')
          .in('course_id', ids);
        if (holeCounts) {
          const counts: Record<string, number> = {};
          for (const row of holeCounts) {
            counts[row.course_id] = (counts[row.course_id] ?? 0) + 1;
          }
          setHoleCountsMap(counts);
        }
      }
    } catch {
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCourses(); }, [fetchCourses]);

  const handleDelete = async () => {
    if (!deletingCourse) return;
    try {
      const { error } = await supabase.from('courses').delete().eq('id', deletingCourse.id);
      if (error) throw error;
      toast.success('Course deleted');
      setCourses((prev) => prev.filter((c) => c.id !== deletingCourse.id));
    } catch {
      toast.error('Failed to delete course');
    } finally {
      setDeletingCourse(null);
    }
  };

  if (selectedCourse) {
    return (
      <MobileAdminHoleEditor
        course={selectedCourse}
        onBack={() => { setSelectedCourse(null); fetchCourses(); }}
      />
    );
  }

  return (
    <div className="px-4 py-4 space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">{courses.length} course{courses.length !== 1 ? 's' : ''}</p>
        <button
          onClick={() => { setEditingCourse(null); setSheetOpen(true); }}
          className="bg-golf-orange text-white rounded-xl px-3 py-2.5 flex items-center gap-1.5 text-sm font-medium"
        >
          <PlusIcon className="w-4 h-4" />
          New
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-golf-orange border-t-transparent" />
        </div>
      ) : courses.length === 0 ? (
        <div className="text-center py-16">
          <TrophyIcon className="w-12 h-12 text-gray-200 mx-auto mb-3" />
          <p className="text-gray-400 text-sm">No courses yet</p>
          <button
            onClick={() => { setEditingCourse(null); setSheetOpen(true); }}
            className="mt-3 text-sm text-golf-orange font-medium"
          >
            Add your first course
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {courses.map((course) => {
            const definedHoles = holeCountsMap[course.id] ?? 0;
            const completion = Math.round((definedHoles / course.holes_count) * 100);
            return (
              <div key={course.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl border border-gray-200 overflow-hidden flex-shrink-0 bg-gray-50 flex items-center justify-center">
                    {course.logo_url ? (
                      <img src={course.logo_url} alt={course.name} className="w-full h-full object-contain p-1" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                    ) : (
                      <TrophyIcon className="w-5 h-5 text-gray-300" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-golf-gray text-sm leading-snug truncate">{course.name}</p>
                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${course.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {course.is_active ? 'Active' : 'Inactive'}
                      </span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-golf-sand/50 text-golf-gray font-medium">
                        {course.holes_count}H · Par {course.par}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => { setEditingCourse(course); setSheetOpen(true); }}
                      className="p-2 rounded-xl text-gray-400 hover:text-golf-orange hover:bg-golf-orange/5 transition-colors"
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setDeletingCourse(course)}
                      className="p-2 rounded-xl text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {(course.city || course.country) && (
                  <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-2">
                    <MapPinIcon className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="truncate">{[course.city, course.country].filter(Boolean).join(', ')}</span>
                  </div>
                )}

                <div className="flex items-center gap-2 mb-3">
                  <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                    <div
                      className={`h-1.5 rounded-full transition-all ${completion === 100 ? 'bg-golf-green' : completion > 0 ? 'bg-golf-orange' : 'bg-gray-300'}`}
                      style={{ width: `${completion}%` }}
                    />
                  </div>
                  <span className="text-xs text-gray-400 whitespace-nowrap">{definedHoles}/{course.holes_count} holes</span>
                </div>

                <button
                  onClick={() => setSelectedCourse(course)}
                  className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl bg-gray-50 border border-gray-100 text-xs font-medium text-gray-600 hover:bg-golf-orange/5 hover:text-golf-orange hover:border-golf-orange/20 transition-colors"
                >
                  <AdjustmentsHorizontalIcon className="w-3.5 h-3.5" />
                  Edit Holes & Tee Boxes
                </button>
              </div>
            );
          })}
        </div>
      )}

      <MobileCourseSheet
        isOpen={sheetOpen}
        onClose={() => { setSheetOpen(false); setEditingCourse(null); fetchCourses(); }}
        course={editingCourse}
      />

      <DeleteConfirmationModal
        isOpen={!!deletingCourse}
        title="Delete Course"
        message={deletingCourse ? `Are you sure you want to delete "${deletingCourse.name}"? This will also delete all hole data.` : ''}
        onConfirm={handleDelete}
        onClose={() => setDeletingCourse(null)}
      />
    </div>
  );
}
